import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { useTimeoutFn } from '@vueuse/core'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'

export type FormatPreviewSource = {
  previewScale?: number
  currentCardId?: string | string[]
  isSample?: boolean
}

/**
 * composable 引数用。Vue 2.7 の `defineProps<ImportedType>()` には使わない
 * （compiler-sfc が `@/` import 型を解決できず vue-loader が
 * `Cannot read properties of null (reading 'content')` で落ちる）。
 */
export type FormatPreviewProps = FormatPreviewSource & {
  formatData: Record<string, any>
  basicData: Record<string, any>
  idPrefix?: string
}

export function useFormatPreview(source: FormatPreviewSource) {
  const { deviceLib } = useHostServices()
  const cardChanged = ref(false)
  const highlightTimer = useTimeoutFn(
    () => {
      cardChanged.value = false
    },
    5000,
    { immediate: false }
  )

  const scaleStyle = computed(() => ({
    transform: `scale(${source.previewScale ?? 1})`,
    'transform-origin': 'top left'
  }))

  const highlightMessage = computed(() =>
    deviceLib.isMobileDevice ? 'タップで編集' : 'クリックで編集'
  )

  function goCardTo(id: string) {
    if (source.isSample) return
    emitter.emit('go-card-to', id)
  }

  function highLightClass(cardId: string | string[]) {
    if (source.isSample) return {}
    const current = source.currentCardId ?? ''
    if (typeof cardId === 'string') {
      return {
        highlight: current === cardId && cardChanged.value
      }
    }
    if (Array.isArray(cardId)) {
      return {
        highlight: cardId.indexOf(current as string) !== -1 && cardChanged.value
      }
    }
    return {}
  }

  function setJumpId() {
    cardChanged.value = true
    highlightTimer.stop()
    highlightTimer.start()
  }

  onBeforeMount(() => {
    emitter.on('show-preview-from-card', setJumpId)
  })

  onBeforeUnmount(() => {
    emitter.off('show-preview-from-card', setJumpId)
    highlightTimer.stop()
  })

  return {
    cardChanged,
    scaleStyle,
    highlightMessage,
    goCardTo,
    highLightClass
  }
}
